import { useEffect, useRef, useState } from 'react';
import type { DocumentSummary } from '../api/types';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { ErrorBanner } from './ui/ErrorBanner';

/** Same limits as the server's rename route. */
export const TITLE_MAX = 200;
/** Control, invisible (zero-width, BOM), text-direction and line-separator characters, as the server refuses. */
const FORBIDDEN = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;

/** Rename a document's display title; the original filename on its versions is kept. */
export function RenameDialog({ doc, open, onClose, onSubmit }: { doc: DocumentSummary; open: boolean; onClose: () => void; onSubmit: (title: string) => Promise<unknown> }) {
  const [title, setTitle] = useState(doc.title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const trimmed = title.normalize('NFC').trim();
  const valid = trimmed.length > 0 && trimmed.length <= TITLE_MAX && !FORBIDDEN.test(trimmed);

  useEffect(() => {
    if (!open) return;
    setTitle(doc.title);
    setError(null);
    queueMicrotask(() => input.current?.select());
  }, [open, doc.title]);

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="Rename"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid} busy={busy} onClick={() => void save()}>
            Rename
          </Button>
        </>
      }
    >
      <label className="block">
        <span className="text-xs font-medium text-muted-foreground">Title</span>
        <input
          ref={input}
          className="gsl-input mt-1"
          value={title}
          aria-label="Title"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            }
          }}
        />
      </label>
      <p className="mt-2 text-xs text-muted-foreground">
        Changes the name shown in ERP Attach. The original filename{doc.currentVersion ? ` (${doc.currentVersion.originalFilename})` : ''} is kept.
      </p>
      {trimmed.length > TITLE_MAX ? <p className="mt-1 text-xs text-destructive">Keep it to {TITLE_MAX} characters.</p> : null}
      {error ? (
        <div className="mt-2">
          <ErrorBanner error={error} />
        </div>
      ) : null}
    </Dialog>
  );
}
